import './SegmentedControl.css';

/**
 * SegmentedControl — pill-дорожка с сегментами, где активный подсвечивается
 * заливкой. В отличие от `IconToggleGroup` (самостоятельные icon-кнопки для
 * бинарных выборов), здесь сегменты живут в общей дорожке.
 *
 * ## Почему у компонента два варианта
 *
 * В аппе жили ДВЕ независимые реализации одного контрола: этот атом и
 * приватная копия внутри `print/PrintControls.jsx`. Атом при этом не
 * использовал никто — его снесли как мёртвый в #1329, после чего копия
 * осталась единственной. Сведены обратно в один компонент: разметка и
 * поведение теперь общие, а расхождение во внешнем виде вынесено в проп
 * `variant`, чтобы оно было видимым решением, а не двумя случайно разными
 * файлами.
 *
 * - `brand` (по умолчанию) — активный сегмент залит брендовым цветом, белый
 *   текст, высота 40px. Это канон `docs/design/component-parity.md` §6 и
 *   то же, что делает `b2b-segmented` в панели.
 * - `subtle` — активный сегмент белый на сером треке, тёмный текст, кегль
 *   12px. Так выглядит sidebar печати, и значения перенесены оттуда
 *   один-в-один, чтобы печатная поверхность у дилера не сдвинулась.
 *
 * ⚠️ `subtle` канону не соответствует. Он оставлен, потому что печатная
 * поверхность уезжает дилерам, и менять её вид попутно с рефакторингом
 * нельзя. Свести варианты — отдельное решение дизайна (#1329).
 *
 * @param {object} props
 * @param {string} props.value — текущее выбранное значение.
 * @param {(value: string) => void} props.onChange — handler смены.
 * @param {Array<{value: string, label?: string, icon?: React.ComponentType, ariaLabel?: string}>} props.options
 *   `icon` — компонент из @phosphor-icons/react. Сегмент может быть icon-only: тогда
 *   подпись для screen-reader берётся из `ariaLabel`.
 * @param {'brand' | 'subtle'} [props.variant] — внешний вид, см. выше.
 * @param {string} [props.ariaLabel] — общий label для radiogroup.
 * @param {string} [props.className] — дополнительный CSS класс.
 */
export const SegmentedControl = ({
  value,
  onChange,
  options,
  variant = 'brand',
  ariaLabel,
  className = '',
}) => (
  <div
    className={`segmented-control segmented-control--${variant} ${className}`.trim()}
    role="radiogroup"
    aria-label={ariaLabel}
  >
    {options.map((opt) => {
      const Icon = opt.icon;
      const isActive = opt.value === value;
      // Icon-only сегмент обязан нести подпись: иначе кнопка «без имени».
      const a11yLabel = opt.ariaLabel || opt.label || opt.value;
      return (
        <button
          key={opt.value}
          type="button"
          role="radio"
          aria-checked={isActive}
          aria-label={a11yLabel}
          title={Icon && !opt.label ? a11yLabel : undefined}
          className={`segmented-control__segment${isActive ? ' segmented-control__segment--active' : ''}${
            Icon && !opt.label ? ' segmented-control__segment--icon' : ''
          }`}
          onClick={() => onChange?.(opt.value)}
        >
          {Icon ? (
            <Icon
              className="segmented-control__icon"
              size={variant === 'subtle' ? 20 : 18}
              weight={variant === 'subtle' ? 'regular' : 'bold'}
              aria-hidden="true"
            />
          ) : null}
          {opt.label ? <span className="segmented-control__label">{opt.label}</span> : null}
        </button>
      );
    })}
  </div>
);

export default SegmentedControl;
